// Telif cozumu: once yalniz metin (baslik/aciklama/etiket) degisikligi yeterli mi —
// asil hedef ana yapiya dokunmadan temizlemek. Tasarimda risk varsa tasarim degisiklikleri
// listelenir; bu talimat analizden yapilan yeni tasarim uretimlerine otomatik eklenir.

import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

export interface CopyrightFix {
  status: "running" | "ready" | "failed"; error?: string;
  strategy?: "text_only" | "text_and_design"; summaryTr?: string;
  replacements?: Array<{ from: string; to: string }>;
  title?: string; description?: string; tags?: string[];
  designRisk?: boolean; designChangesTr?: string[]; designBrief?: string;
  publicDomain?: { available: boolean; briefEn: string; noteTr: string; riskTr: string };
  designMode?: "original" | "public_domain";
  applied?: { at: string; original: { title: string; description: string; tags: string[] } };
  warnings?: string[]; at: string;
}

async function call(path: string, body?: unknown) {
  const r = await fetch(`${getBackendUrl()}/api/analyses${path}`, {
    method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const j = await r.json().catch(() => null);
  if (!r.ok) throw new Error(j?.error ?? `İstek başarısız (${r.status})`);
}

export function CopyrightFixPanel({ analysisId, level, fix, current, onChanged }: {
  analysisId: string; level: string; fix: CopyrightFix | null;
  current: { title: string; tags: string[] }; onChanged: () => void;
}) {
  const running = fix?.status === "running" && Date.now() - Date.parse(fix.at) < 5 * 60_000;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(onChanged, 4000);
    return () => clearInterval(t);
  }, [running]);
  const gen = useMutation({ mutationFn: () => call(`/${analysisId}/copyright-fix`), onSuccess: onChanged });
  const apply = useMutation({ mutationFn: () => call(`/${analysisId}/copyright-fix/apply`), onSuccess: onChanged });
  const revert = useMutation({ mutationFn: () => call(`/${analysisId}/copyright-fix/revert`), onSuccess: onChanged });
  const mode = useMutation({ mutationFn: (m: "original" | "public_domain") => call(`/${analysisId}/copyright-fix/design-mode`, { mode: m }), onSuccess: onChanged });
  const err = (gen.error ?? apply.error ?? revert.error ?? mode.error) as Error | null;

  if (!fix || fix.status === "failed" || (fix.status === "running" && !running)) {
    // Istege bagli: telif dusukse kart gosterilmez (USPTO tescilli marka bulursa seviye zaten yukselir)
    if (level === "low" && !fix) return null;
    return (
      <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
        <p className="text-sm font-medium text-gray-900">Telif çözümü</p>
        <p className="mt-0.5 text-xs text-gray-600">Önce yalnız başlık, açıklama ve etiket değiştirilerek (ana yapı korunarak) riskten kaçılabilir mi bakılır; tasarımda risk varsa tasarım için de değişiklik önerilir. İsteğe bağlıdır (yaklaşık 30 sn, birkaç sent); derin inceleme yaptıysan bulguları da kullanılır.</p>
        {fix?.status === "failed" && <p className="mt-1 text-xs text-red-600">{fix.error ?? "Üretilemedi."}</p>}
        <button onClick={() => gen.mutate()} disabled={gen.isPending}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
          {gen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Telif çözümü üret
        </button>
        {err && <p className="mt-1 text-xs text-red-600">{err.message}</p>}
      </div>
    );
  }
  if (running) {
    return <p className="mt-4 flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 text-sm text-gray-700"><Loader2 className="h-4 w-4 animate-spin" /> Telif çözümü hazırlanıyor… (yaklaşık 30 sn)</p>;
  }

  const textOnly = fix.strategy === "text_only";
  return (
    <div className="mt-4 space-y-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        <p className="text-sm font-semibold text-gray-900">Telif çözümü</p>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${textOnly ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
          {textOnly ? "Yalnız metin değişikliği yeterli" : "Metin + tasarım değişmeli"}
        </span>
        {fix.applied && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white"><CheckCircle2 className="h-3 w-3" /> Metin uygulandı</span>}
      </div>
      {fix.summaryTr && <p className="text-sm text-gray-700">{fix.summaryTr}</p>}

      {fix.replacements && fix.replacements.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fix.replacements.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200">
              <span className="text-red-600 line-through">{r.from}</span><ArrowRight className="h-3 w-3 text-gray-400" /><span className="text-emerald-700">{r.to}</span>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-2 rounded-lg bg-white p-3 text-sm ring-1 ring-gray-200">
        <div>
          <p className="text-xs font-medium text-gray-500">Temiz başlık</p>
          <p className="text-gray-900">{fix.title}</p>
          {!fix.applied && current.title !== fix.title && <p className="mt-0.5 text-xs text-gray-400 line-through">{current.title}</p>}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">Temiz etiketler ({fix.tags?.length ?? 0}/13)</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {(fix.tags ?? []).map((t) => (
              <span key={t} className={`rounded-full px-2 py-0.5 text-xs ${current.tags.some((c) => c.toLowerCase() === t.toLowerCase()) ? "bg-gray-100 text-gray-600" : "bg-emerald-100 text-emerald-800"}`}>{t}</span>
            ))}
          </div>
        </div>
        <details>
          <summary className="cursor-pointer text-xs font-medium text-gray-500">Temiz açıklama</summary>
          <p className="mt-1 whitespace-pre-line text-xs text-gray-700">{fix.description}</p>
        </details>
      </div>

      {fix.warnings && fix.warnings.length > 0 && fix.warnings.map((w) => (
        <p key={w} className="flex items-start gap-1 text-xs text-amber-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {w}</p>
      ))}

      {fix.designRisk && (
        <div className="rounded-lg bg-amber-50 p-3 ring-1 ring-amber-200">
          <p className="text-xs font-semibold text-amber-900">Tasarımda risk var — değiştirilecekler</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-900">
            {(fix.designChangesTr ?? []).map((c, i) => <li key={i}>{c}</li>)}
          </ul>
          {fix.publicDomain?.available && (
            <div className="mt-2 space-y-1.5 rounded-lg bg-white p-2 ring-1 ring-amber-200">
              <p className="text-xs font-semibold text-gray-900">Tasarım yönü</p>
              {([["original", "Özgün karakter (en güvenli)", "Yukarıdaki değişikliklerle yeni bir karakter çizilir."],
                 ["public_domain", "Kamu malı sürüm", fix.publicDomain.noteTr]] as const).map(([k, label, note]) => (
                <label key={k} className="flex cursor-pointer items-start gap-2 text-xs text-gray-800">
                  <input type="radio" name={`dm-${analysisId}`} checked={(fix.designMode ?? "original") === k} disabled={mode.isPending}
                    onChange={() => mode.mutate(k)} className="mt-0.5 border-gray-300 text-indigo-600 focus:ring-indigo-200" />
                  <span><span className="font-medium">{label}</span>{note ? <span className="block text-gray-600">{note}</span> : null}</span>
                </label>
              ))}
              {fix.designMode === "public_domain" && fix.publicDomain.riskTr && (
                <p className="flex items-start gap-1 text-xs font-medium text-red-700"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> {fix.publicDomain.riskTr}</p>
              )}
            </div>
          )}
          <p className="mt-1.5 text-xs text-amber-800">Bu analizden üretilen yeni tasarımlara (atölye, toplu üretim, custom stüdyo, tasarım seti) seçili yönün talimatı otomatik eklenir. Daha önce üretilmiş tasarımlar değişmez; yeniden üret.</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!fix.applied ? (
          <button onClick={() => apply.mutate()} disabled={apply.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:bg-emerald-300">
            {apply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Temiz metni uygula
          </button>
        ) : (
          <button onClick={() => revert.mutate()} disabled={revert.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {revert.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Eski metne dön
          </button>
        )}
        <button onClick={() => gen.mutate()} disabled={gen.isPending} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600">
          <RotateCcw className="h-3.5 w-3.5" /> Yeniden üret
        </button>
        <span className="text-xs text-gray-500">Uygulanınca bu analizden açılan yeni taslaklar temiz başlık, açıklama ve etiketleri kullanır.</span>
      </div>
      {err && <p className="text-xs text-red-600">{err.message}</p>}
    </div>
  );
}
